import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  makeStyles,
  Slide,
  Switch,
  TextField
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch } from 'react-redux';
import { transmitMultiActiveStatus } from 'src/features/deviceSlice';
import TransmissionConfigDialog from './TransmissionConfigDialog';

const useStyles = makeStyles(theme => ({
  alertModalScrollPaper: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1)
    }
  },
  alertModalPaper: {
    width: '100%',
    maxWidth: '820px',
    margin: 0,
    borderRadius: '4px',
    overflow: 'hidden'
  },
  alertModalHeader: {
    background: '#d9042f',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 14px',
    fontSize: '15px',
    fontWeight: 700
  },
  alertModalClose: {
    color: '#fff',
    padding: 0
  },
  alertModalContent: {
    padding: '10px 10px 12px'
  },
  alertLeadText: {
    margin: 0,
    color: '#f44336',
    fontStyle: 'italic',
    fontSize: '14px',
    lineHeight: 1.4
  },
  alertSectionTitle: {
    color: '#0d2b4f',
    fontSize: '14px',
    fontWeight: 700,
    margin: '6px 0'
  },
  alertSearchRow: {
    marginBottom: '8px'
  },
  alertSearchInput: {
    background: '#fff',
    '& .MuiOutlinedInput-root': {
      minHeight: '36px'
    },
    '& .MuiOutlinedInput-input': {
      padding: '9px 12px',
      fontSize: '13px'
    }
  },
  alertTableWrapper: {
    height: '34vh',
    minHeight: '300px',
    maxHeight: '400px',
    overflow: 'auto',
    border: '1px solid #d4d9de',
    [theme.breakpoints.down('sm')]: {
      minHeight: '240px',
      height: '30vh'
    }
  },
  alertTable: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      background: '#e9edf1',
      color: '#283949',
      fontWeight: 700
    },
    '& th, & td': {
      border: '1px solid #d4d9de',
      padding: '6px',
      fontSize: '12px',
      verticalAlign: 'middle',
      textAlign: 'center'
    }
  },
  alertTableLeft: {
    textAlign: 'left !important'
  },
  alertFooter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    marginTop: '8px'
  },
  alertAcknowledgeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    paddingLeft: '0px',
    color: '#283949',
    fontSize: '13px'
  },
  alertButtons: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    gap: '10px'
  },
  primaryActionButton: {
    minWidth: '72px',
    height: '34px',
    background: '#d9042f',
    color: '#fff',
    fontSize: '13px',
    textTransform: 'none',
    '&:hover': {
      background: '#d9042f'
    }
  },
  secondaryActionButton: {
    minWidth: '72px',
    height: '34px',
    background: '#e9edf1',
    color: '#283949',
    fontSize: '13px',
    textTransform: 'none',
    '&:hover': {
      background: '#dce2e8'
    }
  },
  transmissionSwitch: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#d9042f'
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#d9042f'
    }
  }
}));

const formatTransmissionOffAt = value => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hourCycle: 'h23'
  }).format(date);
};

const AlertModalTransition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} timeout={{ enter: 360, exit: 220 }} {...props} />
));

const AlertTransmissionModal = ({ defaultDevices, dismissStorageKey, getListDevice, onTransmissionUpdated, open, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [configDialogRow, setConfigDialogRow] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [transmissionStatuses, setTransmissionStatuses] = useState({});
  const [submittingTransmissionIds, setSubmittingTransmissionIds] = useState({});
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [devices, setDevices] = useState(defaultDevices || []);

  useEffect(() => {
    if (!open) {
      setDontShowAgain(false);
      setConfigDialogRow(null);
      setSelectedDeviceIds([]);
      setVehicleSearch('');
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDevices(defaultDevices || []);
  }, [defaultDevices, open]);

  useEffect(() => {
    setTransmissionStatuses(
      (devices || []).reduce((acc, device) => {
        acc[device.id] = Boolean(device.status_transmission);
        return acc;
      }, {})
    );
  }, [devices]);

  const refreshDevices = async () => {
    const response = await getListDevice({
      keywords: vehicleSearch.trim() || undefined
    });

    setDevices(response?.payload?.devices || []);
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (!vehicleSearch.trim()) {
      setDevices(defaultDevices || []);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        await refreshDevices();
      } catch (error) {
        setDevices([]);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [defaultDevices, getListDevice, open, vehicleSearch]);

  const handleToggleTransmission = row => async (_, checked) => {
    if (!checked) {
      setTransmissionStatuses(prevState => ({
        ...prevState,
        [row.id]: false
      }));
      return;
    }

    setTransmissionStatuses(prevState => ({
      ...prevState,
      [row.id]: true
    }));
    setConfigDialogRow(row);
  };

  const handleCloseConfigDialog = () => {
    if (configDialogRow) {
      setTransmissionStatuses(prevState => ({
        ...prevState,
        [configDialogRow.id]: Boolean(configDialogRow.status_transmission)
      }));
    }

    setConfigDialogRow(null);
  };

  const handleToggleConfirmTransmission = deviceId => event => {
    const checked = event.target.checked;

    setSelectedDeviceIds(prevState =>
      checked
        ? [...new Set([...prevState, deviceId])]
        : prevState.filter(id => id !== deviceId)
    );
  };

  const handleTransmissionConfigSuccess = async deviceId => {
    setConfigDialogRow(null);
    setSubmittingTransmissionIds(prevState => ({
      ...prevState,
      [deviceId]: false
    }));
    await refreshDevices();
    if (onTransmissionUpdated) {
      await onTransmissionUpdated();
    }
  };

  const handleSave = async () => {
    if (selectedDeviceIds.length > 0) {
      await dispatch(
        transmitMultiActiveStatus({
          device_ids: selectedDeviceIds,
          status: 2
        })
      ).unwrap();

      setSelectedDeviceIds([]);
      await refreshDevices();
      if (onTransmissionUpdated) {
        await onTransmissionUpdated();
      }
    }

    if (dismissStorageKey) {
      if (dontShowAgain) {
        localStorage.setItem(dismissStorageKey, 'true');
      } else {
        localStorage.removeItem(dismissStorageKey);
      }
    }

    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={AlertModalTransition}
        classes={{ scrollPaper: classes.alertModalScrollPaper }}
        PaperProps={{ className: classes.alertModalPaper }}
      >
        <div className={classes.alertModalHeader}>
          <span>Danh sách cảnh báo truyền C08 - BCA</span>
          <IconButton onClick={onClose} className={classes.alertModalClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className={classes.alertModalContent}>
          <p className={classes.alertLeadText}>
            Danh sách dưới đây gồm các phương tiện hiện đang bỏ tích truyền dữ liệu lên Bộ công an. Kính đề nghị Quý khách kiểm tra và xác nhận lại thông tin để tránh phát sinh vi phạm do không truyền dữ liệu về Bộ Công an theo quy định.
          </p>

          <div className={classes.alertSectionTitle}>
            {`Danh sách xe bỏ tích truyền BCA (${devices.length})`}
          </div>

          <div className={classes.alertSearchRow}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              className={classes.alertSearchInput}
              placeholder="Tìm kiếm phương tiện"
              value={vehicleSearch}
              onChange={event => setVehicleSearch(event.target.value)}
            />
          </div>

          <div className={classes.alertTableWrapper}>
            <table className={classes.alertTable}>
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Phương tiện</th>
                  <th>Ngày bỏ tích truyền</th>
                  <th>Người bỏ tích truyền</th>
                  <th>Truyền BCA</th>
                  <th>Xác nhận bỏ truyền</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((row, index) => (
                  <tr key={`${row.id}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{row?.vehicle?.license_plate}</td>
                    <td>{formatTransmissionOffAt(row.transmission_off_at)}</td>
                    <td>{row?.transmission_off_by?.full_name || ''}</td>
                    <td>
                      <Switch
                        size="small"
                        checked={Boolean(transmissionStatuses[row.id])}
                        onChange={handleToggleTransmission(row)}
                        className={classes.transmissionSwitch}
                        disabled={
                          Boolean(submittingTransmissionIds[row.id]) ||
                          selectedDeviceIds.includes(row.id)
                        }
                      />
                    </td>
                    <td>
                      <Checkbox
                        size="small"
                        checked={selectedDeviceIds.includes(row.id)}
                        onChange={handleToggleConfirmTransmission(row.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={classes.alertFooter}>
            <div className={classes.alertAcknowledgeRow}>
              <Checkbox
                size="small"
                checked={dontShowAgain}
                onChange={event => setDontShowAgain(event.target.checked)}
              />
              <span>Đã nhận thông tin và không hiển thị lại cảnh báo này !</span>
            </div>
            <div className={classes.alertButtons}>
              <Button variant="contained" className={classes.primaryActionButton} onClick={handleSave}>
                Lưu lại
              </Button>
              <Button variant="contained" className={classes.secondaryActionButton} onClick={onClose}>
                Hủy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <TransmissionConfigDialog
        open={Boolean(configDialogRow)}
        row={configDialogRow}
        onClose={handleCloseConfigDialog}
        onSuccess={handleTransmissionConfigSuccess}
      />
    </>
  );
};

AlertTransmissionModal.propTypes = {
  defaultDevices: PropTypes.array,
  dismissStorageKey: PropTypes.string,
  getListDevice: PropTypes.func.isRequired,
  onTransmissionUpdated: PropTypes.func,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AlertTransmissionModal;
